import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';

import { ApiContext } from '@shared/contexts';
import { GENERIC_ERROR_MESSAGE, SUPPORT_NOTIFIED_MESSAGE } from '@shared/i18n';
import { toApiException } from '@shared/helpers/toApiException';
import { impossibleState } from '@shared/helpers/impossibleState';
import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import { useGetBlob } from '@modules/documents/api';
import {
  createFrontendContentHooks,
  createFrontendRuntime,
  createFrontendRuntimeSide,
} from '@modules/documents/components/changesRuntime';
import { applyImagePreprocessing } from '@modules/documents/components/changesHtmlUtils';
import useEditorActive from '@modules/documents/hooks/useEditorActive.hook';
import useScrollContent from '@modules/documents/hooks/useScrollContent.hook';
import { makeBlockHeaderRenderer } from '@modules/documents/largeDocument/static/blockHeader';
import { isAiEditRevertable } from '@modules/documents/tiptap/plugins/AiEditReviewPlugin';
import { DropdownOptionsStep } from '@modules/documents/tiptap/nodes/templateField/DropdownOptionsStep';
import { SignInContext, useLog } from '@modules/users';
import type { TUseDocumentResult } from '@modules/documents/types/documents.types';

import useAiAssistantStore from '../stores/useAiAssistantStore';
import isConnectionError from '../helpers/isConnectionError.helper';
import type { TAiAssistantStatusMessage, TAiAssistantSurface, TAiRunFeedbackInput, TAiAssistantOrigin } from '../types';
import {
  AI_NO_ANSWER_FOUND_TITLE,
  AI_NOTHING_TO_CHANGE_TITLE,
  AI_OUT_OF_SCOPE_TITLE,
  AI_UNCLEAR_REQUEST_TITLE,
} from '../constants';

import { prepareAiEdit } from './prepareAiEdit';
import { buildAiEditTransaction } from './aiEditTransaction';
import useAiEditRevertShortcut from './useAiEditRevertShortcut.hook';
import useAiEditPreviewLinkClicks from './useAiEditPreviewLinkClicks.hook';
import { mergeRunData, newRunCacheIds, withoutRunData, type TAiEditRunCache } from './aiEditRunCache';
import {
  AI_EDIT_DECISIONS_CONNECTION_LOST_MESSAGE,
  AI_EDIT_DECISIONS_FAILED_MESSAGE,
  AI_EDIT_SUPPORT_NOTE,
} from './constants';
import { useCancelAiEditReview, useSubmitAiEditDecisions } from './api';
import { EAiEditResultType, type TAiEditReviewResult, type TAiEditRunResponse } from './types';

type TAiEditReviewPhase = 'idle' | 'submitting' | 'applying' | 'reverting';

const toDecisionsFailedMessage = (error: unknown): string =>
  isConnectionError(error) ? AI_EDIT_DECISIONS_CONNECTION_LOST_MESSAGE : AI_EDIT_DECISIONS_FAILED_MESSAGE;

const toNoSuggestionStatusMessage = (
  resultType: Exclude<EAiEditResultType, EAiEditResultType.ANSWER>
): TAiAssistantStatusMessage => {
  if (resultType === EAiEditResultType.OUT_OF_SCOPE) {
    return { text: AI_OUT_OF_SCOPE_TITLE, intent: 'warning' };
  }
  if (resultType === EAiEditResultType.NO_ANSWER_FOUND) {
    return { text: AI_NO_ANSWER_FOUND_TITLE, intent: 'warning' };
  }
  if (resultType === EAiEditResultType.UNCLEAR_REQUEST) {
    return { text: AI_UNCLEAR_REQUEST_TITLE, intent: 'warning' };
  }
  return { text: AI_NOTHING_TO_CHANGE_TITLE, intent: 'info' };
};

type TParams = {
  surface: TAiAssistantSurface;
  origin: TAiAssistantOrigin | undefined;
};

/** Owns the local editor review from plugin activation through Accept, Reject, or Revert. */
const useAiEditReview = ({ surface, origin }: TParams) => {
  const { show, queryClient } = useContext(ApiContext);
  const { organization } = useContext(SignInContext);
  const documentContext = useContext(DocumentContext);
  const scrollContent = useScrollContent();
  useAiEditPreviewLinkClicks(documentContext?.editor);
  const { read, update } = useAiAssistantStore();
  const { followUpPrompt: storedFollowUpPrompt } = read(surface, origin);
  const logMutation = useLog();
  const getBlob = useGetBlob();
  const submitDecisions = useSubmitAiEditDecisions();
  const cancelAiEdit = useCancelAiEditReview();
  const isReviewActiveRef = useRef(false);
  const reviewSessionRef = useRef(0);
  const focusFrameRef = useRef<number>();
  // The references and blocks the review merged into the cached document; a review that ends without accepting takes them back out.
  const runCacheRef = useRef<TAiEditRunCache>();
  const [phase, setPhase] = useState<TAiEditReviewPhase>('idle');
  const [result, setResult] = useState<TAiEditReviewResult>();
  const [submittedFeedback, setSubmittedFeedback] = useState<TAiRunFeedbackInput | null>(null);
  const [statusMessage, setStatusMessage] = useState<TAiAssistantStatusMessage | null>(null);
  const [changeIndex, setChangeIndex] = useState(0);
  const canRevert = useEditorActive(
    (editor) => result?.status === 'accepted' && isAiEditRevertable(editor.state),
    false
  );

  const clearScheduledFocus = useCallback(() => {
    if (focusFrameRef.current !== undefined) window.cancelAnimationFrame(focusFrameRef.current);
    focusFrameRef.current = undefined;
  }, []);

  const end = useCallback(() => {
    documentContext?.editor?.commands.endAiEditReview();
    isReviewActiveRef.current = false;
    reviewSessionRef.current += 1;
    const runCache = runCacheRef.current;
    runCacheRef.current = undefined;
    if (runCache) {
      queryClient.setQueryData(
        [`document-${runCache.docId}`],
        (oldData: TUseDocumentResult): TUseDocumentResult => ({
          ...oldData,
          doc: withoutRunData(oldData.doc, runCache),
        })
      );
    }
  }, []);

  const begin = useCallback((): boolean => {
    const editor = documentContext?.editor;
    if (!editor?.commands.beginAiEditReview()) {
      setStatusMessage({ text: GENERIC_ERROR_MESSAGE, intent: 'error' });
      return false;
    }
    isReviewActiveRef.current = true;
    reviewSessionRef.current += 1;
    return true;
  }, [documentContext?.editor]);

  const fail = useCallback(
    (message?: string) => {
      // A terminal failure belongs to an open review; nothing to report once it has ended.
      if (!isReviewActiveRef.current) return;
      end();
      if (message) setStatusMessage({ text: message, intent: 'error' });
    },
    [end]
  );

  const focusAndScroll = useCallback(
    (elementId: string) => {
      if (!documentContext?.editor?.commands.focusAiEditChange(elementId)) return;
      scrollContent({ attribute: 'data-ai-edit-element-id', value: elementId, noFocus: true });
    },
    [scrollContent]
  );

  const prepareCompletedRun = useCallback(
    async (serverRun: TAiEditRunResponse) => {
      const editor = documentContext?.editor;
      const response = serverRun.result;
      const reviewSession = reviewSessionRef.current;
      try {
        if (!serverRun.resultType || !response) throw new Error('MISSING_RESULT');
        if (!editor) throw new Error('EDITOR_UNAVAILABLE');

        if (serverRun.resultType !== EAiEditResultType.ANSWER) {
          if (response.suggestions.length > 0) throw new Error('UNEXPECTED_AI_EDIT_SUGGESTIONS');
          end();
          const isMock = serverRun.mockResponse && serverRun.resultType === EAiEditResultType.NOTHING_TO_CHANGE;
          const noChangeMessage = toNoSuggestionStatusMessage(serverRun.resultType);
          if (!isMock && !response.answer.trim()) {
            setStatusMessage(noChangeMessage);
            return;
          }
          setSubmittedFeedback(null);
          setStatusMessage(null);
          setResult({
            ...(isMock ? { status: 'mock-response' } : { status: 'no-change', statusMessage: noChangeMessage }),
            runId: serverRun.runId,
            answer: response.answer,
            citationSources: response.citationSources,
          });
          return;
        }
        if (response.suggestions.length === 0) throw new Error('MISSING_AI_EDIT_SUGGESTIONS');

        // Edit mode guarantees a draft, so both of these exist here.
        const version = documentContext.version ?? impossibleState('AI edit review without a version');
        const versionId = documentContext.versionId ?? impossibleState('AI edit review without a version id');

        // Without the run's references and blocks in the cached document, the node views would register them again on accept.
        const { doc, resolvedItemNames } = mergeRunData(
          documentContext.doc,
          documentContext.resolvedItemNames,
          response
        );
        if (doc !== documentContext.doc) {
          runCacheRef.current = { docId: doc._id, ...newRunCacheIds(documentContext.doc, response) };
          queryClient.setQueryData(
            [`document-${doc._id}`],
            (oldData: TUseDocumentResult): TUseDocumentResult => ({
              ...oldData,
              ...mergeRunData(oldData.doc, oldData.resolvedItemNames, response),
            })
          );
        }
        const renderBlockHeader = makeBlockHeaderRenderer({
          doc,
          version,
          resolvedItemNames,
          versionId,
        });
        const { plan, preview, navigationElementIds } = await prepareAiEdit(response.suggestions, editor, {
          createRuntime: ({ current, previous }) =>
            createFrontendRuntime(
              doc,
              createFrontendRuntimeSide(doc, version, current.html, current.headings),
              createFrontendRuntimeSide(doc, version, previous.html, previous.headings),
              getBlob,
              renderBlockHeader
            ),
          hooks: createFrontendContentHooks(),
          headingStart: version.config?.headingStart,
          isH5RefEnabled: organization.mock?.isH5RefEnabled,
          preprocessHtml: (html) =>
            applyImagePreprocessing(documentContext.doc._id, documentContext.permissions.token, html),
        });
        if (reviewSessionRef.current !== reviewSession) return;
        if (!editor.commands.showAiEditPreview(preview)) throw new Error('PREVIEW_REJECTED');
        setSubmittedFeedback(null);
        setResult({
          status: 'review',
          runId: serverRun.runId,
          answer: response.answer,
          citationSources: response.citationSources,
          followUpPrompt: response.followUpPrompt,
          plan,
          navigationElementIds,
          dropdownOptions: response.templateFieldOptions,
        });
        setChangeIndex(0);
        if (navigationElementIds[0]) {
          clearScheduledFocus();
          focusFrameRef.current = requestAnimationFrame(() => {
            focusFrameRef.current = undefined;
            if (isReviewActiveRef.current) focusAndScroll(navigationElementIds[0]);
          });
        }
      } catch (error) {
        if (reviewSessionRef.current !== reviewSession) return;
        const code = error instanceof Error ? error.message : 'UNKNOWN';
        logMutation.mutate({
          message: `AI_EDIT_PREPARE_FAILURE code=${code} documentId=${documentContext.doc._id} runId=${serverRun.runId}`,
        });
        setStatusMessage({ text: SUPPORT_NOTIFIED_MESSAGE, intent: 'error' });
        end();
      }
    },
    [clearScheduledFocus, documentContext, end, focusAndScroll, logMutation, organization.mock?.isH5RefEnabled]
  );

  const cancel = useCallback(() => {
    clearScheduledFocus();
    reviewSessionRef.current += 1;
    if (isReviewActiveRef.current) end();
    // Only an IN_REVIEW run accepts a cancel PUT - a run still preparing has no server state to cancel yet.
    if (result?.status === 'review') cancelAiEdit.mutate(result.runId);
    setResult((current) => (current?.status === 'review' ? undefined : current));
    setPhase('idle');
    setChangeIndex(0);
  }, [clearScheduledFocus, end, result]);

  const accept = async () => {
    if (result?.status !== 'review' || !documentContext) return;
    const { runId, plan, answer, citationSources, followUpPrompt, dropdownOptions } = result;
    const editor = documentContext?.editor;
    const optionsRef = documentContext.templateDropdownOptionsRef;
    const reviewSession = reviewSessionRef.current;
    clearScheduledFocus();

    // Apply before recording the decision: SUBMITTED is one-way with no reset, so an apply
    // failure after the PUT would record an accepted run that changed nothing.
    setPhase('applying');
    let saved: boolean;
    try {
      if (!editor) throw new Error('REVIEW_ENDED');
      const transaction = buildAiEditTransaction(editor.state, plan);
      if (Object.keys(dropdownOptions).length) {
        transaction.step(DropdownOptionsStep.create(optionsRef.current, dropdownOptions));
      }
      if (!editor.commands.applyAiEditTransaction(transaction)) {
        throw new Error('APPLY_REJECTED');
      }
      // The references and blocks are in the document now, so the review's end keeps them.
      runCacheRef.current = undefined;
      saved = await documentContext.save({ forceSave: true });
    } catch (error) {
      if (reviewSessionRef.current !== reviewSession) return;
      const code = error instanceof Error ? error.message : 'UNKNOWN';
      logMutation.mutate({
        message: `AI_EDIT_APPLY_FAILURE code=${code} documentId=${documentContext.doc._id} runId=${runId}`,
      });
      end();
      setResult(undefined);
      show('error', 'The document changed before the edit could be applied. No AI changes were saved.');
      setPhase('idle');
      return;
    }
    if (reviewSessionRef.current !== reviewSession) return;

    setPhase('submitting');
    try {
      await submitDecisions.mutateAsync({
        runId,
        decisions: plan.suggestions.map(({ id }) => ({ suggestionId: id, accepted: true })),
      });
    } catch (error) {
      if (reviewSessionRef.current !== reviewSession) return;
      const apiError = toApiException(error);
      const code = apiError?.id || (error instanceof Error ? error.message : 'UNKNOWN');
      logMutation.mutate({
        message: `AI_EDIT_DECISION_FAILURE action=accept code=${code} documentId=${documentContext.doc._id} runId=${runId}`,
      });
      show('error', apiError?.message || toDecisionsFailedMessage(error));
      setPhase('idle');
      return;
    }
    if (reviewSessionRef.current !== reviewSession) return;

    end();
    setResult({ status: 'accepted', runId, answer, citationSources, saveFailed: !saved });
    update(surface, origin, { followUpPrompt: followUpPrompt || null });
    setPhase('idle');
  };

  const reject = async () => {
    if (result?.status !== 'review') return;
    const { runId, plan } = result;
    const reviewSession = reviewSessionRef.current;
    clearScheduledFocus();
    setPhase('submitting');
    try {
      await submitDecisions.mutateAsync({
        runId,
        decisions: plan.suggestions.map(({ id }) => ({ suggestionId: id, accepted: false })),
      });
    } catch (error) {
      if (reviewSessionRef.current !== reviewSession) return;
      const apiError = toApiException(error);
      const code = apiError?.id || (error instanceof Error ? error.message : 'UNKNOWN');
      logMutation.mutate({
        message: `AI_EDIT_DECISION_FAILURE action=reject code=${code} documentId=${documentContext?.doc._id ?? 'none'} runId=${runId}`,
      });
      show('error', apiError?.message || toDecisionsFailedMessage(error));
      setPhase('idle');
      return;
    }
    if (reviewSessionRef.current !== reviewSession) return;
    end();
    setResult(undefined);
    setChangeIndex(0);
    setPhase('idle');
  };

  const revert = useCallback(async () => {
    if (result?.status !== 'accepted' || !documentContext) return;
    setPhase('reverting');
    if (!documentContext.editor.commands.revertAiEdit()) {
      logMutation.mutate({
        message: `AI_EDIT_REVERT_FAILURE documentId=${documentContext.doc._id} runId=${result.runId}`,
      });
      show('error', `The AI changes could not be reverted. ${AI_EDIT_SUPPORT_NOTE}`);
      setPhase('idle');
      return;
    }

    setResult(undefined);
    setChangeIndex(0);
    setPhase('idle');
    update(surface, origin, { followUpPrompt: null });
    await documentContext.save({ forceSave: true });
  }, [documentContext, result, show, surface, origin, update]);

  const navigate = (nextIndex: number) => {
    if (result?.status !== 'review') return;
    const elementId = result.navigationElementIds[nextIndex];
    if (!elementId) return;
    setChangeIndex(nextIndex);
    focusAndScroll(elementId);
  };

  useAiEditRevertShortcut(
    result?.status === 'accepted' && canRevert && phase === 'idle',
    documentContext?.editor,
    revert
  );

  useEffect(
    () => () => {
      clearScheduledFocus();
      if (isReviewActiveRef.current) end();
    },
    [clearScheduledFocus, end]
  );

  return {
    isActive: isReviewActiveRef.current || phase !== 'idle' || result?.status === 'review',
    isBusy: phase !== 'idle',
    canRevert,
    result,
    followUpPrompt: storedFollowUpPrompt,
    statusMessage,
    changeIndex,
    submittedFeedback,
    setSubmittedFeedback,
    begin,
    fail,
    prepareCompletedRun,
    cancel,
    accept,
    reject,
    revert,
    navigate,
    clearResult: () => {
      setResult(undefined);
      setSubmittedFeedback(null);
      setStatusMessage(null);
      update(surface, origin, { followUpPrompt: null });
    },
  };
};

export default useAiEditReview;
